import type { NextRequest } from "next/server";
import {
  ARTICLE_BY_SLUG,
  ARTICLE_META,
  type ArticleSlug,
} from "@/lib/articles/meta";
import { isPreviewArticlePath } from "@/lib/articles/preview-issues";
import {
  ISSUES_VIEW_COOKIE,
  issuesViewRewriteVariant,
} from "@/lib/issues-view-preferences";
import type { Reader } from "@/lib/reader";

export const HOME_PATHS = [
  "/magazine",
  "/onboarding",
  "/welcome",
  "/resubscribe",
] as const;

export type HomePath = (typeof HOME_PATHS)[number];

export interface RoutePolicy {
  match: (path: string) => boolean;
  redirect?: (reader: Reader) => string | null;
  rewrite?: (request: NextRequest) => URL | null;
}

const ISSUE_PATHS = new Set(ARTICLE_META.map((item) => item.link));

const ROUTES: RoutePolicy[] = [
  {
    match: (path) => path === "/",
    redirect: (reader) => (reader.isAuthenticated ? homeFor(reader) : null),
  },
  { match: isPreviewArticlePath },
  {
    match: (path) => ISSUE_PATHS.has(path),
    redirect: (reader) =>
      reader.isAuthenticated ? stayIfHome("/magazine")(reader) : "/",
  },
  {
    match: (path) => path.startsWith("/magazine"),
    redirect: stayIfHome("/magazine"),
    rewrite: magazineViewRewrite,
  },
  {
    match: (path) => path.startsWith("/onboarding"),
    redirect: stayIfHome("/onboarding"),
  },
  { match: (path) => path === "/welcome", redirect: stayIfHome("/welcome") },
  {
    match: (path) => path === "/resubscribe",
    redirect: stayIfHome("/resubscribe"),
  },
];

const DEFAULT_ROUTE: RoutePolicy = { match: () => true };

const TRAILING_SLASH = /\/$/;
const UNDECODABLE_PATH_POLICY_PATH = "/magazine";

function decodePath(path: string): string {
  try {
    return decodeURIComponent(path);
  } catch {
    return UNDECODABLE_PATH_POLICY_PATH;
  }
}

export function routeFor(path: string): RoutePolicy {
  const decoded = decodePath(path);
  const normalised =
    decoded === "/" ? decoded : decoded.replace(TRAILING_SLASH, "");
  return ROUTES.find((route) => route.match(normalised)) ?? DEFAULT_ROUTE;
}

function magazineViewRewrite(request: NextRequest): URL | null {
  if (request.nextUrl.pathname !== "/magazine") {
    return null;
  }
  const variant = issuesViewRewriteVariant(
    request.cookies.get(ISSUES_VIEW_COOKIE)?.value
  );
  if (!variant) {
    return null;
  }
  const target = request.nextUrl.clone();
  target.pathname = `/magazine/views/${variant}`;
  return target;
}

export function canReadMagazine(reader: Reader): boolean {
  return homeFor(reader) === "/magazine";
}

export function canReadIssue(reader: Reader, slug: ArticleSlug): boolean {
  return ARTICLE_BY_SLUG[slug].free === true || canReadMagazine(reader);
}

function homeFor(reader: Reader): HomePath | "/login" {
  if (!reader.isAuthenticated) {
    return "/login";
  }
  if (reader.hasActiveSubscription) {
    return reader.onboardingCompleted ? "/magazine" : "/onboarding";
  }
  return reader.hasEverSubscribed ? "/resubscribe" : "/welcome";
}

function stayIfHome(path: HomePath) {
  return (reader: Reader): string | null => {
    const home = homeFor(reader);
    return home === path ? null : home;
  };
}
